import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { api, fileUrl, inr, buildWaLink, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/meta";
import { SpinWheel } from "@/components/site/SpinWheel";
import { useSettings } from "@/context/SettingsContext";
import { useCustomer } from "@/context/CustomerContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Minus, Plus, Loader2, CheckCircle2, QrCode, Copy, ShieldCheck, TicketPercent, CreditCard, Smartphone, Gift } from "lucide-react";

const postToGateway = ({ action, fields }) => {
  const form = document.createElement("form");
  form.method = "POST"; form.action = action;
  Object.entries(fields).forEach(([k, v]) => { const i = document.createElement("input"); i.type = "hidden"; i.name = k; i.value = String(v); form.appendChild(i); });
  document.body.appendChild(form); form.submit();
};

const SHARINGS = ["quad", "triple", "double"];
const SHARING_LABEL = { quad: "Quad sharing", triple: "Triple sharing", double: "Double sharing" };

export function BookingDialog({ trip, children }) {
  const { settings } = useSettings();
  const perPerson = settings.advance_per_person || 1000;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const departures = trip?.departures || [];
  const openDeps = departures.filter((d) => !d.sold_out);
  const [rooms, setRooms] = useState({ quad: 1, triple: 0, double: 0 });
  const pickups = trip?.pickups || [];
  const [pickup, setPickup] = useState(pickups[0]?.name || "");
  const pickupExtra = Number(pickups.find((p) => p.name === pickup)?.extra || 0);
  const travellers = rooms.quad + rooms.triple + rooms.double;
  const [departure, setDeparture] = useState(openDeps[0]?.date || "");
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", transaction_ref: "", screenshot_url: null });
  const [result, setResult] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [couponSeed] = useState(() => Math.random().toString(36).slice(2, 14));
  const [coupon, setCoupon] = useState(null);
  const [wheel, setWheel] = useState(null);
  const [checking, setChecking] = useState(false);
  const [payMode, setPayMode] = useState(settings.online_payments ? "online" : "upi_manual");
  const { customer } = useCustomer();
  useEffect(() => { if (customer && open) setForm((f) => ({ ...f, name: f.name || customer.name || "", whatsapp: f.whatsapp || customer.whatsapp || "", email: f.email || customer.email || "" })); }, [customer, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [refInput, setRefInput] = useState("");
  const [vInput, setVInput] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [referral, setReferral] = useState(null);
  const selDep = departures.find((d) => d.date === departure);
  const seatsLeft = selDep?.seats_left ?? null;
  const baseAdvance = useMemo(() => perPerson * Math.max(1, travellers), [perPerson, travellers]);
  const prices = selDep?.prices || trip?.prices || { quad: 0, triple: 0, double: 0 };
  const tripTotal = SHARINGS.reduce((a, k) => a + (rooms[k] || 0) * Number(prices[k] || 0), 0) + pickupExtra * travellers;
  const discount = (coupon?.discount || 0) + (referral ? Math.min(referral.discount, tripTotal - (coupon?.discount || 0)) : 0);
  const finalTotal = tripTotal - discount;
  const voucherUsed = voucher ? Math.min(voucher.balance, finalTotal) : 0;
  const advance = Math.max(0, baseAdvance - Math.max(0, voucherUsed - (finalTotal - baseAdvance)));
  const applyVoucher = async () => {
    if (!vInput.trim()) return;
    try { const { data } = await api.get(`/vouchers/validate/${encodeURIComponent(vInput.trim().toUpperCase())}`); setVoucher(data); toast.success(`Gift voucher applied · ${inr(data.balance)} available`); }
    catch (err) { setVoucher(null); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const balanceDue = Math.max(0, finalTotal - voucherUsed - advance);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => { setStep(1); setResult(null); setForm({ name: "", whatsapp: "", email: "", transaction_ref: "", screenshot_url: null }); setRooms({ quad: 1, triple: 0, double: 0 }); setCoupon(null); setCouponInput(""); setReferral(null); setRefInput(""); setPayMode(settings.online_payments ? "online" : "upi_manual"); };

  useEffect(() => {
    const saved = localStorage.getItem("safarshala_ref");
    if (open && saved && !referral && !refInput) { setRefInput(saved); applyReferral(saved); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyReferral = async (code) => {
    const c = typeof code === "string" ? code : refInput;
    if (!c.trim()) return;
    try {
      const { data } = await api.get(`/referrals/validate/${encodeURIComponent(c.trim().toUpperCase())}`);
      setReferral(data);
      toast.success(`Referral from ${data.referrer} applied — ${inr(data.discount)} off`);
    } catch (err) { setReferral(null); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const payOnline = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) { toast.error("Name and WhatsApp are required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        name: form.name, whatsapp: form.whatsapp, email: form.email, payment_mode: "online", trip_id: trip.id, trip_name: trip.name, departure_date: departure,
        travellers, rooms, pickup_point: pickup || "", coupon_code: coupon?.code || "", coupon_seed: couponSeed, referral_code: referral?.code || "", voucher_code: voucher?.code || "",
      });
      if (data.status === "Confirmed") { setResult(data); setStep(3); setLoading(false); track("Purchase", { content_ids: [trip.id], content_name: trip.name, content_type: "product", value: data.final_total || 0, currency: "INR" }, `purchase:${data.id}`); return; }
      const init = await api.post("/payments/payu/initiate", { booking_ref: data.id });
      postToGateway(init.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput, trip_id: trip.id, departure_date: departure, travellers, rooms, pickup_point: pickup, coupon_seed: couponSeed });
      if (data.random && data.value_max != null && data.value_max > data.value_min) { setWheel(data); return; }
      setCoupon(data);
      toast.success(`Coupon applied — you save ${inr(data.discount)} on the trip`);
    } catch (err) {
      setCoupon(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setChecking(false); }
  };

  const changeRoom = (k, delta) => {
    const next = { ...rooms, [k]: Math.max(0, (rooms[k] || 0) + delta) };
    const total = next.quad + next.triple + next.double;
    if (total < 1) return;
    const max = seatsLeft ?? Infinity;
    if (total > max) { toast.error(`Only ${max} seat${max === 1 ? "" : "s"} left on this date`); return; }
    setRooms(next);
    setCoupon(null);
  };

  const uploadShot = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("screenshot_url", data.url);
      toast.success("Screenshot uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp || !form.transaction_ref) {
      toast.error("Name, WhatsApp and Transaction Reference are required");
      return;
    }
    if (!/^\d{12}$/.test(form.transaction_ref)) {
      toast.error("Transaction Reference (UTR) must be exactly 12 digits");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form, trip_id: trip.id, trip_name: trip.name, departure_date: departure,
        travellers, rooms, pickup_point: pickup || departures.find((d) => d.date === departure)?.pickup_point || "",
        coupon_code: coupon?.code || "", coupon_seed: couponSeed, referral_code: referral?.code || "", voucher_code: voucher?.code || "", payment_mode: "upi_manual",
      });
      setResult(data);
      setStep(4);
      track("Purchase", { content_ids: [trip.id], content_name: trip.name, content_type: "product", value: data.final_total || 0, currency: "INR", num_items: travellers }, `purchase:${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>

    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTimeout(reset, 200); }}>
      <div onClick={() => { setOpen(true); track("InitiateCheckout", { content_ids: [trip.id], content_name: trip.name, content_type: "product", value: trip.starting_price || 0, currency: "INR" }); }}>{children}</div>
      <DialogContent className="max-w-md max-h-[92dvh] overflow-y-auto overscroll-contain p-5 sm:p-6 max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-3xl max-sm:w-full max-sm:max-w-full max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))]" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl sm:text-3xl text-left pr-8 leading-tight">{step === 4 ? "Booking Received" : "Book " + (trip?.name || "")}</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>Departure Date</Label>
              <Select value={departure} onValueChange={(v) => { setDeparture(v); setRooms({ quad: 1, triple: 0, double: 0 }); setCoupon(null); }}>
                <SelectTrigger data-testid="booking-departure"><SelectValue placeholder="Select date" /></SelectTrigger>
                <SelectContent>
                  {departures.map((d) => (
                    <SelectItem key={d.id} value={d.date} disabled={d.sold_out} data-testid="booking-departure-option">
                      {d.date} — from {inr(d.from_price)}{d.sold_out ? " · Sold Out" : d.seats_left != null && d.seats_left <= 5 ? ` · ${d.seats_left} left` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selDep && seatsLeft != null && (
                <p className={`text-xs mt-1 ${seatsLeft <= 5 ? "text-amber-600 dark:text-amber-400 font-medium" : "text-muted-foreground"}`} data-testid="booking-seats-left">
                  {seatsLeft <= 5 ? `Hurry — only ${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} left` : `${seatsLeft} seats available`}
                </p>
              )}
              {!openDeps.length && <p className="text-xs text-destructive mt-1" data-testid="booking-all-sold-out">All departures are currently sold out. Query on WhatsApp for the next batch.</p>}
            </div>
            {pickups.length > 0 && (
              <div>
                <Label>Pickup Location</Label>
                <Select value={pickup} onValueChange={(v) => { setPickup(v); setCoupon(null); }}>
                  <SelectTrigger data-testid="booking-pickup"><SelectValue placeholder="Select pickup" /></SelectTrigger>
                  <SelectContent>
                    {pickups.map((p) => (
                      <SelectItem key={p.name} value={p.name} data-testid="booking-pickup-option">
                        {p.name}{p.extra > 0 ? ` · +${inr(p.extra)}/person` : p.extra < 0 ? ` · −${inr(Math.abs(p.extra))}/person` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Travellers & Room Sharing</Label>
              <div className="mt-1 space-y-2" data-testid="room-selector">
                {SHARINGS.map((k) => (
                  <div key={k} className="flex items-center justify-between rounded-xl border px-3 py-2" data-testid={`room-${k}`}>
                    <div>
                      <p className="font-medium capitalize">{SHARING_LABEL[k]}</p>
                      <p className="text-xs text-muted-foreground">{inr(prices[k])} / person</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" data-testid={`room-${k}-minus`} onClick={() => changeRoom(k, -1)} disabled={!rooms[k]}><Minus className="h-3.5 w-3.5" /></Button>
                      <span className="font-mono text-lg w-6 text-center" data-testid={`room-${k}-count`}>{rooms[k]}</span>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" data-testid={`room-${k}-plus`} onClick={() => changeRoom(k, 1)}><Plus className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1"><span className="font-medium text-foreground" data-testid="travellers-count">{travellers}</span> traveller{travellers === 1 ? "" : "s"} total</p>
            </div>
            <div>
              <Label>Coupon Code</Label>
              <div className="flex gap-2 mt-1">
                <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="e.g. SAFAR10" className="font-mono uppercase" data-testid="coupon-input" disabled={!!coupon} />
                {coupon ? (
                  <Button type="button" variant="outline" onClick={() => { setCoupon(null); setCouponInput(""); }} data-testid="coupon-remove">Remove</Button>
                ) : (
                  <Button type="button" variant="outline" onClick={applyCoupon} disabled={checking || !couponInput} data-testid="coupon-apply">{checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button>
                )}
              </div>
              {coupon && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1" data-testid="coupon-applied"><TicketPercent className="h-3.5 w-3.5" /> {coupon.code} applied · {coupon.type === "percent" ? `${coupon.value}% off` : `${inr(coupon.value)} off`}</p>}
            </div>
            <div>
              <Label className="flex items-center gap-1"><Gift className="h-3.5 w-3.5 text-gold" /> Friend's Referral Code <span className="text-muted-foreground font-normal">· {inr(settings.referral_friend_discount || 500)} off</span></Label>
              <div className="flex gap-2 mt-1">
                <Input value={refInput} onChange={(e) => setRefInput(e.target.value.toUpperCase())} placeholder="e.g. RAHU7F2A" className="font-mono uppercase" data-testid="referral-input" disabled={!!referral} />
                {referral ? <Button type="button" variant="outline" onClick={() => { setReferral(null); setRefInput(""); }} data-testid="referral-remove">Remove</Button>
                  : <Button type="button" variant="outline" onClick={() => applyReferral()} disabled={!refInput} data-testid="referral-apply">Apply</Button>}
              </div>
              {referral && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1" data-testid="referral-applied">Referred by {referral.referrer} · −{inr(referral.discount)}</p>}
            </div>
            <div>
              <Label>Gift Voucher Code</Label>
              <div className="flex gap-2 mt-1">
                <Input value={vInput} onChange={(e) => setVInput(e.target.value.toUpperCase())} placeholder="GIFT-XXXX-XXXX" className="font-mono uppercase" data-testid="voucher-input" disabled={!!voucher} />
                {voucher ? <Button type="button" variant="outline" onClick={() => { setVoucher(null); setVInput(""); }}>Remove</Button> : <Button type="button" variant="outline" onClick={applyVoucher} disabled={!vInput} data-testid="voucher-apply">Apply</Button>}
              </div>
              {voucher && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1" data-testid="voucher-applied">Voucher {voucher.code} · −{inr(voucherUsed)}{voucher.balance > voucherUsed ? ` (${inr(voucher.balance - voucherUsed)} stays on the voucher)` : ""}</p>}
            </div>
            <div className="rounded-xl bg-muted p-4 space-y-1.5 text-sm" data-testid="price-summary">
              {pickupExtra !== 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Pickup: {pickup}</span><span data-testid="pickup-extra">{pickupExtra > 0 ? "+" : "−"}{inr(Math.abs(pickupExtra) * travellers)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Trip total ({SHARINGS.filter((k) => rooms[k]).map((k) => `${rooms[k]} ${SHARING_LABEL[k]}`).join(", ")})</span><span data-testid="trip-total">{inr(tripTotal)}</span></div>
              {coupon && <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>Coupon {coupon.code}</span><span data-testid="discount-amount">−{inr(coupon.discount)}</span></div>}
              {referral && <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>Referral {referral.code}</span><span data-testid="referral-discount">−{inr(referral.discount)}</span></div>}
              {voucher && <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>Gift voucher</span><span>−{inr(voucherUsed)}</span></div>}
              {(discount > 0 || voucher) && <div className="flex justify-between font-medium"><span>{voucher ? "Payable after voucher" : "Total after discount"}</span><span data-testid="final-total">{inr(finalTotal - voucherUsed)}</span></div>}
              <div className="flex items-end justify-between pt-2 border-t border-border">
                <div>
                  <p className="text-muted-foreground">Pay now — advance ({inr(perPerson)} × {travellers})</p>
                  <p className="font-heading text-3xl text-primary" data-testid="advance-amount">{inr(advance)}</p>
                </div>
                <ShieldCheck className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="text-xs text-muted-foreground">Balance {inr(balanceDue)} payable before departure.</p>
            </div>
            <Button className="w-full rounded-full" data-testid="booking-to-payment" disabled={!openDeps.length} onClick={() => { if (!departure) { toast.error("Select a departure date"); return; } setStep(2); }}>Proceed to Payment</Button>
          </div>
        )}

        {step === 2 && settings.online_payments && (
          <div className="grid grid-cols-2 gap-2" data-testid="pay-mode-toggle">
            {[["online", "Pay Online", CreditCard, "UPI · Cards · NetBanking"], ["upi_manual", "UPI QR + UTR", Smartphone, "Scan & share reference"]].map(([k, l, Icon, sub]) => (
              <button key={k} type="button" onClick={() => setPayMode(k)} data-testid={`pay-mode-${k}`}
                className={`rounded-xl border p-3 text-left transition-colors ${payMode === k ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted"}`}>
                <Icon className={`h-5 w-5 mb-1 ${payMode === k ? "text-primary" : "text-muted-foreground"}`} /><p className="text-sm font-medium">{l}</p><p className="text-[11px] text-muted-foreground">{sub}</p>
              </button>
            ))}
          </div>
        )}

        {step === 2 && payMode === "online" && settings.online_payments && (
          <form onSubmit={payOnline} className="space-y-3" data-testid="online-pay-form">
            <div className="rounded-xl border p-4 text-left text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Trip</span><span className="font-medium">{trip?.name} · {departure}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Travellers</span><span className="font-medium">{travellers}</span></div>
              {discount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discounts</span><span className="font-medium text-emerald-600 dark:text-emerald-400">−{inr(discount)}</span></div>}
              <div className="flex justify-between text-base pt-1"><span>Advance to pay now</span><span className="font-heading text-primary text-xl" data-testid="online-advance">{inr(advance)}</span></div>
            </div>
            <div><Label>Name *</Label><Input data-testid="online-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>WhatsApp *</Label><Input data-testid="online-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="91XXXXXXXXXX" /></div>
              <div><Label>Email</Label><Input data-testid="online-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="pay-online-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1" /> Pay {inr(advance)} securely</>}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">Powered by PayU · You'll be redirected to complete payment. Booking confirms instantly on success.</p>
          </form>
        )}

        {step === 2 && !(payMode === "online" && settings.online_payments) && (
          <div className="space-y-4 text-center">
            <div className="rounded-xl border p-4 text-left text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Trip</span><span className="font-medium">{trip?.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Departure</span><span className="font-medium">{departure}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Travellers</span><span className="font-medium">{travellers} · {SHARINGS.filter((k) => rooms[k]).map((k) => `${rooms[k]} ${SHARING_LABEL[k].split(" ")[0]}`).join(", ")}</span></div>
              {pickup && <div className="flex justify-between"><span className="text-muted-foreground">Pickup</span><span className="font-medium">{pickup}{pickupExtra ? ` (${pickupExtra > 0 ? "+" : "−"}${inr(Math.abs(pickupExtra))}/person)` : ""}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Trip total</span><span className="font-medium">{inr(tripTotal)}</span></div>
              {coupon && <div className="flex justify-between"><span className="text-muted-foreground">Coupon {coupon.code}</span><span className="font-medium text-emerald-600 dark:text-emerald-400">−{inr(discount)}</span></div>}
              {coupon && <div className="flex justify-between"><span className="text-muted-foreground">After discount</span><span className="font-medium">{inr(finalTotal)}</span></div>}
              <div className="flex justify-between text-base pt-1"><span>Advance to pay now</span><span className="font-heading text-primary text-xl">{inr(advance)}</span></div>
            </div>
            <div className="flex flex-col items-center gap-2">
              {settings.qr_url ? (
                <img src={fileUrl(settings.qr_url)} alt="UPI QR" className="h-48 w-48 rounded-xl border object-contain bg-white" data-testid="upi-qr" />
              ) : (
                <div className="h-48 w-48 rounded-xl border flex flex-col items-center justify-center bg-muted text-muted-foreground" data-testid="upi-qr">
                  <QrCode className="h-12 w-12 mb-2" /><span className="text-xs px-4 text-center">Scan the UPI ID below to pay</span>
                </div>
              )}
              <button type="button" onClick={() => { navigator.clipboard.writeText(settings.upi_id || ""); toast.success("UPI ID copied"); }}
                className="flex items-center gap-2 font-mono text-sm bg-muted px-3 py-1.5 rounded-full" data-testid="upi-id">
                {settings.upi_id || "safarshala@upi"} <Copy className="h-3.5 w-3.5" />
              </button>
              <p className="text-xs text-muted-foreground max-w-xs">Pay the advance using any UPI app, then continue and enter your transaction reference.</p>
            </div>
            <Button className="w-full rounded-full" data-testid="payment-done" onClick={() => setStep(3)}>I've Paid — Continue</Button>
          </div>
        )}

        {step === 3 && (
          <form onSubmit={submitBooking} className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="booking-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>WhatsApp *</Label><Input data-testid="booking-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="91XXXXXXXXXX" /></div>
              <div><Label>Email</Label><Input data-testid="booking-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            </div>
            <div>
              <Label>UPI Transaction Ref / UTR (12 digits) *</Label>
              <Input data-testid="booking-txn" inputMode="numeric" maxLength={12} className="font-mono tracking-widest" value={form.transaction_ref}
                onChange={(e) => set("transaction_ref", e.target.value.replace(/\D/g, "").slice(0, 12))} placeholder="e.g. 412345678901" />
              <p className={`text-xs mt-1 ${form.transaction_ref.length === 12 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid="booking-txn-hint">
                {form.transaction_ref.length === 12 ? "✓ Valid 12-digit UTR" : `${form.transaction_ref.length}/12 digits — find it in your UPI app's payment details`}
              </p>
            </div>
            <div>
              <Label>Payment Screenshot (optional)</Label>
              <Input type="file" accept="image/*" data-testid="booking-screenshot" onChange={(e) => uploadShot(e.target.files?.[0])} />
              {form.screenshot_url && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">✓ Uploaded</p>}
            </div>
            <div className="rounded-lg bg-muted p-3 flex justify-between text-sm">
              <span>Advance Amount{coupon ? ` · ${coupon.code} saves ${inr(discount)} on trip` : ""}</span><span className="font-heading text-primary text-lg">{inr(advance)}</span>
            </div>
            <Button type="submit" disabled={loading} data-testid="booking-submit" className="w-full rounded-full">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Booking"}
            </Button>
          </form>
        )}

        {step === 4 && result && (
          <div className="text-center space-y-4 py-2">
            <CheckCircle2 className="h-16 w-16 text-emerald-600 dark:text-emerald-400 mx-auto" />
            <div>
              <p className="font-heading text-2xl">Payment Verification Pending</p>
              <p className="text-sm text-muted-foreground mt-1">Your booking for <b>{trip?.name}</b> is received. Our team will verify your payment and confirm shortly with your Booking ID.</p>
            </div>
            <div className="rounded-xl border p-4 text-left text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Departure</span><span>{result.departure_date}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Travellers</span><span>{result.travellers} · {(result.price_breakup || []).map((b) => `${b.count} ${b.sharing}`).join(", ")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Trip total</span><span>{inr(result.trip_total)}</span></div>
              {result.discount_amount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Coupon {result.coupon_code}</span><span className="text-emerald-600 dark:text-emerald-400">−{inr(result.discount_amount)}</span></div>}
              {result.discount_amount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Total after discount</span><span className="font-medium">{inr(result.final_total)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Balance due later</span><span>{inr(result.balance_due)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Advance Paid</span><span className="font-medium">{inr(result.advance_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="text-amber-600 dark:text-amber-400 font-medium">{result.status}</span></div>
            </div>
            <Button variant="outline" className="w-full rounded-full" data-testid="booking-whatsapp-confirm"
              onClick={() => window.open(buildWaLink(settings.whatsapp_number, `Hi ${settings.brand_name || "Safarshala"}, I've just booked ${trip?.name} for ${result.departure_date} (${result.travellers} travellers). Transaction Ref: ${result.transaction_ref}. Please verify my payment.`), "_blank")}>
              Share on WhatsApp
            </Button>
            <Button className="w-full rounded-full" data-testid="booking-close" onClick={() => setOpen(false)}>Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
      {wheel && <SpinWheel result={wheel} onDone={() => { setCoupon(wheel); toast.success(`Coupon applied — you save ${inr(wheel.discount)} on the trip`); setWheel(null); }} />}
    </>
  );
}
